import { fromEventPattern, merge, Observable, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import {
  AllEventTypes,
  IMarker as _IMarker,
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map';

import { GoogleMap } from '../../../map';
import { IGoogleMarker } from '../interfaces/google-marker.interface';

type IMarker = _IMarker<GoogleMap>;

/**
 * Service responsible for event handling operations on Google Map markers.
 * Manages event listeners and converts Google Maps events to RxJS observables.
 */
@Injectable()
export class GoogleMarkerEventService {
  private readonly _destroy$ = new Subject<void>();
  private readonly _googleMarkers = new Map<string, IGoogleMarker>();
  private readonly _programmaticEvents$ = new Subject<IMarkerEventUnion<GoogleMap>>();

  setMarkersCollection(markers: Map<string, IGoogleMarker>): void {
    this._googleMarkers.clear();
    markers.forEach((marker, id) => {
      this._googleMarkers.set(id, marker);
    });
  }

  onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'click', this._createClickEvent.bind(this));
  }

  onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'dblclick', this._createDoubleClickEvent.bind(this));
  }

  onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'rightclick',
      this._createRightClickEvent.bind(this),
    );
  }

  onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'mouseover', this._createMouseOverEvent.bind(this));
  }

  onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'mouseout', this._createMouseOutEvent.bind(this));
  }

  onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'mouseenter',
      this._createMouseEnterEvent.bind(this),
    );
  }

  onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'mouseleave',
      this._createMouseLeaveEvent.bind(this),
    );
  }

  onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'dragstart', this._createDragStartEvent.bind(this));
  }

  onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'drag', this._createDragEvent.bind(this));
  }

  onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'dragend', this._createDragEndEvent.bind(this));
  }

  onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'positionchange',
      this._createPositionChangeEvent.bind(this),
    );
  }

  onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'titlechange',
      this._createTitleChangeEvent.bind(this),
    );
  }

  onMarkerVisibilityChange(marker: IMarker): Observable<IMarkerVisibilityChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'visibilitychange',
      this._createVisibilityChangeEvent.bind(this),
    );
  }

  onMarkerSelectionChange(marker: IMarker): Observable<IMarkerSelectionChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'selectionchange',
      this._createSelectionChangeEvent.bind(this),
    );
  }

  onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'iconchange',
      this._createIconChangeEvent.bind(this),
    );
  }

  onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<GoogleMap>> {
    return this._createEventObservable(
      marker,
      'stylechange',
      this._createStyleChangeEvent.bind(this),
    );
  }

  onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'add', this._createAddEvent.bind(this));
  }

  onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'remove', this._createRemoveEvent.bind(this));
  }

  onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<GoogleMap>> {
    return this._createEventObservable(marker, 'destroy', this._createDestroyEvent.bind(this));
  }

  onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<GoogleMap>> {
    return merge(
      this.onMarkerClick(marker),
      this.onMarkerDoubleClick(marker),
      this.onMarkerRightClick(marker),
      this.onMarkerMouseOver(marker),
      this.onMarkerMouseOut(marker),
      this.onMarkerMouseEnter(marker),
      this.onMarkerMouseLeave(marker),
      this.onMarkerDragStart(marker),
      this.onMarkerDrag(marker),
      this.onMarkerDragEnd(marker),
      this.onMarkerPositionChange(marker),
      this.onMarkerTitleChange(marker),
      this.onMarkerVisibilityChange(marker),
      this.onMarkerSelectionChange(marker),
      this.onMarkerIconChange(marker),
      this.onMarkerStyleChange(marker),
      this.onMarkerAdd(marker),
      this.onMarkerRemove(marker),
      this.onMarkerDestroy(marker),
    ).pipe(
      catchError((error) => {
        console.error('Error in marker event stream:', error);
        return new Observable<IMarkerEventUnion<GoogleMap>>();
      }),
    )
  }


  fireMarkerEvent(eventType: string, marker: IMarker, eventData?: unknown): void {
  const event = {
    type: eventType as AllEventTypes,
    marker,
    timestamp: Date.now(),
    data: eventData || {},
  } as IMarkerEventUnion<GoogleMap>;

    this._programmaticEvents$.next(event);
  }

  onProgrammaticEvents(): Observable<IMarkerEventUnion<GoogleMap>> {
    return this._programmaticEvents$.asObservable().pipe(takeUntil(this._destroy$));
  }

  destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._programmaticEvents$.complete();
  }

  private _getGoogleMarker(marker: IMarker): IGoogleMarker | null {
    return this._googleMarkers.get(marker.id) || null;
  }

  private _createEventObservable<T>(
    marker: IMarker,
    eventName: string,
    eventCreator: (marker: IMarker) => T,
  ): Observable<T> {
    const googleMarker = this._getGoogleMarker(marker);
    if (!googleMarker) return new Observable();

    return fromEventPattern(
      (handler) => googleMarker.getMarker().addListener(eventName, handler),
      (handler, listener) => listener.remove(),
    ).pipe(
      map(() => eventCreator(marker)),
      takeUntil(this._destroy$),
      shareReplay(1),
    );
  }

  private _createClickEvent(marker: IMarker): IMarkerClickEvent<GoogleMap> {
    return {
      type: 'click',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('click'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
        button: 'left',
      },
    };
  }

  private _createDoubleClickEvent(marker: IMarker): IMarkerDoubleClickEvent<GoogleMap> {
    return {
      type: 'doubleclick',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('dblclick'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
        button: 'left',
      },
    };
  }

  private _createRightClickEvent(marker: IMarker): IMarkerRightClickEvent<GoogleMap> {
    return {
      type: 'rightclick',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('contextmenu'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
        button: 'right',
      },
    };
  }

  private _createMouseOverEvent(marker: IMarker): IMarkerMouseOverEvent<GoogleMap> {
    return {
      type: 'mouseover',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('mouseover'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
      },
    };
  }

  private _createMouseOutEvent(marker: IMarker): IMarkerMouseOutEvent<GoogleMap> {
    return {
      type: 'mouseout',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('mouseout'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
      },
    };
  }

  private _createDragStartEvent(marker: IMarker): IMarkerDragStartEvent<GoogleMap> {
    return {
      type: 'dragstart',
      marker,
      timestamp: Date.now(),
      data: {
        position: marker.position,
        previousPosition: marker.position,
        delta: { lat: 0, lng: 0 },
      },
    };
  }

  private _createDragEvent(marker: IMarker): IMarkerDragMoveEvent<GoogleMap> {
    return {
      type: 'drag',
      marker,
      timestamp: Date.now(),
      data: {
        position: marker.position,
        previousPosition: marker.position,
        delta: { lat: 0, lng: 0 },
      },
    };
  }

  private _createDragEndEvent(marker: IMarker): IMarkerDragEndEvent<GoogleMap> {
    return {
      type: 'dragend',
      marker,
      timestamp: Date.now(),
      data: {
        position: marker.position,
        previousPosition: marker.position,
        delta: { lat: 0, lng: 0 },
      },
    };
  }

  private _createAddEvent(marker: IMarker): IMarkerAddEvent<GoogleMap> {
    return {
      type: 'add',
      marker,
      timestamp: Date.now(),
      data: {
        reason: 'programmatic',
      },
    };
  }

  private _createRemoveEvent(marker: IMarker): IMarkerRemoveEvent<GoogleMap> {
    return {
      type: 'remove',
      marker,
      timestamp: Date.now(),
      data: {
        reason: 'programmatic',
      },
    };
  }

  private _createDestroyEvent(marker: IMarker): IMarkerDestroyEvent<GoogleMap> {
    return {
      type: 'destroy',
      marker,
      timestamp: Date.now(),
      data: {
        reason: 'programmatic',
      },
    };
  }

  private _createMouseEnterEvent(marker: IMarker): IMarkerMouseEnterEvent<GoogleMap> {
    return {
      type: 'mouseenter',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('mouseenter'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
      },
    };
  }

  private _createMouseLeaveEvent(marker: IMarker): IMarkerMouseLeaveEvent<GoogleMap> {
    return {
      type: 'mouseleave',
      marker,
      timestamp: Date.now(),
      data: {
        event: new MouseEvent('mouseleave'),
        coordinates: {
          lat: marker.position.lat,
          lng: marker.position.lon,
        },
      },
    };
  }

  private _createPositionChangeEvent(marker: IMarker): IMarkerPositionChangeEvent<GoogleMap> {
    return {
      type: 'positionchange',
      marker,
      timestamp: Date.now(),
      data: {
        current: marker.position,
        previous: marker.position,
        changed: true,
      },
    };
  }

  private _createTitleChangeEvent(marker: IMarker): IMarkerTitleChangeEvent<GoogleMap> {
    return {
      type: 'titlechange',
      marker,
      timestamp: Date.now(),
      data: {
        current: marker.title,
        previous: marker.title,
        changed: true,
      },
    };
  }

  private _createVisibilityChangeEvent(marker: IMarker): IMarkerVisibilityChangeEvent<GoogleMap> {
    return {
      type: 'visibilitychange',
      marker,
      timestamp: Date.now(),
      data: {
        current: marker.visible,
        previous: marker.visible,
        changed: true,
      },
    };
  }

  private _createSelectionChangeEvent(marker: IMarker): IMarkerSelectionChangeEvent<GoogleMap> {
    return {
      type: 'selectionchange',
      marker,
      timestamp: Date.now(),
      data: {
        current: marker.selected,
        previous: marker.selected,
        changed: true,
      },
    };
  }

  private _createIconChangeEvent(marker: IMarker): IMarkerIconChangeEvent<GoogleMap> {
    const getIconString = (icon: unknown): string => {
      if (typeof icon === 'string') return icon;
      if (icon && typeof icon === 'object') {
        const iconObj = icon as Record<string, unknown>;
        return (iconObj.icon || iconObj.name || iconObj.url || '') as string;
      }
      return '';
    };

    return {
      type: 'iconchange',
      marker,
      timestamp: Date.now(),
      data: {
        current: getIconString(marker.icon),
        previous: getIconString(marker.icon),
        changed: true,
      },
    };
  }

  private _createStyleChangeEvent(marker: IMarker): IMarkerStyleChangeEvent<GoogleMap> {
    return {
      type: 'stylechange',
      marker,
      timestamp: Date.now(),
      data: {
        current: marker.styles as Record<string, unknown>,
        previous: marker.styles as Record<string, unknown>,
        changed: true,
      },
    };
  }
}
